import { t } from './i18n.js';
import { useEffect, useState } from 'react';
import { api, send } from './api.js';
import { Notice, formatStorageBytes, Modal } from './components.js';
const rights = {
  read: 'Lire',
  sendAs: 'Envoyer en tant que',
  sendOnBehalf: 'Envoyer au nom de',
  manage: 'Gérer les messages et dossiers',
  delete: 'Supprimer les messages',
};
export function SharedMailboxes({ notify }: { notify: (text: string) => void }) {
  const [boxes, setBoxes] = useState<any[]>([]),
    [users, setUsers] = useState<any[]>([]),
    [domains, setDomains] = useState<any[]>([]),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [editing, setEditing] = useState<any>(),
    [conversion, setConversion] = useState<any>(),
    [invitation, setInvitation] = useState('');
  const [name, setName] = useState(''),
    [local, setLocal] = useState(''),
    [domain, setDomain] = useState(''),
    [quota, setQuota] = useState('10'),
    [contact, setContact] = useState('');
  async function load() {
    const [b, u, d] = await Promise.all([
      api('/admin/shared-mailboxes'),
      api('/admin/users'),
      api('/admin/domains'),
    ]);
    setBoxes(b);
    setUsers(u);
    setDomains(d);
    setDomain((old) => old || d.find((v: any) => v.ownership === 'verified')?.id || '');
  }
  useEffect(() => {
    void load().catch((e) => setError(e.message));
  }, []);
  async function act(fn: () => Promise<any>, success: string) {
    setBusy(true);
    setError('');
    try {
      const value = await fn();
      await load();
      notify(success);
      return value;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="shared-mailboxes">
      <h2>{t('Boîtes partagées')}</h2>
      <p>
        {t(
          'Aucune unité supplémentaire. Le stockage est prélevé sur le quota commun. Chaque délégué doit posséder une boîte nominative active.',
        )}
      </p>
      {error && <Notice type="error">{error}</Notice>}
      {invitation && (
        <Notice>
          Invitation du nouveau titulaire : <a href={invitation}>{invitation}</a>
        </Notice>
      )}
      <form
        className="shared-create"
        onSubmit={(e) => {
          e.preventDefault();
          void act(
            () =>
              send('/admin/shared-mailboxes', {
                name,
                localPart: local,
                domainId: domain,
                quotaBytes: Math.round(Number(quota) * 1e9),
              }),
            t('Boîte partagée créée'),
          ).then((r) => {
            if (r) {
              setName('');
              setLocal('');
            }
          });
        }}
      >
        <label>
          {t('Nom de la boîte')}
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('Service commercial')}
          />
        </label>
        <label>
          Adresse
          <input
            required
            value={local}
            onChange={(e) => setLocal(e.target.value.toLowerCase())}
            placeholder="commercial"
          />
        </label>
        <label>
          Domaine
          <select value={domain} onChange={(e) => setDomain(e.target.value)}>
            {domains
              .filter((d) => d.ownership === 'verified')
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          Plafond en Go
          <input
            type="number"
            required
            min="2"
            max="200"
            step="0.001"
            value={quota}
            onChange={(e) => setQuota(e.target.value)}
          />
        </label>
        <button className="primary" disabled={busy || !domain}>
          {t('Créer la boîte partagée')}
        </button>
      </form>
      <p>
        {t(
          'Pour consulter dans Thunderbird ou Outlook classique configuré en IMAP, recherchez les dossiers sous',
        )}{' '}
        <strong>{t('Partagees/adresse@entreprise/')}</strong>
        {t(', puis abonnez-vous aux dossiers souhaités avec votre compte nominatif.')}
      </p>
      <button type="button" onClick={() => void load().catch((e) => setError(e.message))}>
        {t('Actualiser les accès IMAP')}
      </button>
      <div className="shared-list">
        {boxes.map((b) => (
          <article key={b.id} className="shared-box">
            <h3>{b.address}</h3>
            {(b.kind === 'shared' || b.nativeImapStatus === 'pending') && (
              <p>
                {t('Accès aux dossiers dans les logiciels de messagerie :')}{' '}
                {b.nativeImapStatus === 'ready'
                  ? t('synchronisé')
                  : b.nativeImapStatus === 'pending'
                    ? t(
                        'synchronisation en attente — les anciens droits peuvent encore être actifs',
                      )
                    : t('non activé sur cet environnement')}
                .
              </p>
            )}
            <p>
              {b.kind === 'shared'
                ? t('Partagée · sans unité supplémentaire')
                : t('Nominative · une unité attribuée')}{' '}
              · {formatStorageBytes(Number(b.used_bytes))} /{' '}
              {formatStorageBytes(Number(b.quota_bytes))}
            </p>
            {b.kind === 'shared' && (
              <>
                <p>{t('{n} délégué(s)', { n: b.delegates.length })}</p>
                <button
                  disabled={busy}
                  onClick={() =>
                    setEditing({
                      ...b,
                      original_quota_bytes: b.quota_bytes,
                      delegates: b.delegates.map((d: any) => ({
                        userId: d.user_id,
                        rights: d.rights,
                      })),
                    })
                  }
                >
                  {t('Gérer les délégations')}
                </button>
              </>
            )}
            <button
              disabled={busy}
              onClick={() => {
                setConversion(b);
                setName(b.name);
                setContact('');
              }}
            >
              {b.kind === 'shared'
                ? t('Convertir en boîte nominative')
                : t('Convertir en boîte partagée')}
            </button>
          </article>
        ))}
      </div>
      {editing && (
        <Modal
          title={t('Délégations — {address}', { address: editing.address })}
          onClose={() => setEditing(undefined)}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void act(
                () =>
                  send(
                    '/admin/shared-mailboxes/' + editing.id,
                    {
                      expectedRevision: editing.revision,
                      delegates: editing.delegates,
                      // Only a new ceiling is sent: delegations stay editable on any box.
                      ...(Number(editing.quota_bytes) !== Number(editing.original_quota_bytes)
                        ? { quotaBytes: Number(editing.quota_bytes) }
                        : {}),
                    },
                    'PATCH',
                  ),
                t('Délégations mises à jour'),
              ).then((r) => {
                if (r) setEditing(undefined);
              });
            }}
          >
            <label>
              {t('Plafond de la boîte en Go')}
              <input
                type="number"
                min={
                  Number(editing.quota_bytes) !== Number(editing.original_quota_bytes)
                    ? '2'
                    : undefined
                }
                max="200"
                step="0.001"
                required
                value={Number(editing.quota_bytes) / 1e9}
                onChange={(e) =>
                  setEditing((old: any) => ({
                    ...old,
                    quota_bytes: Math.round(Number(e.target.value) * 1e9),
                  }))
                }
              />
            </label>
            <p>
              {t(
                'Décocher tous les droits d’une personne révoque son accès. L’administration ne donne aucun accès implicite aux messages.',
              )}
            </p>
            {users
              .filter(
                (u) =>
                  u.status === 'active' &&
                  boxes.some((b) => b.kind === 'personal' && b.owner_id === u.id),
              )
              .map((u) => (
                <fieldset key={u.id}>
                  <legend>
                    {u.name} — {u.email}
                  </legend>
                  {Object.entries(rights).map(([right, label]) => (
                    <label key={right} className="shared-right">
                      <input
                        type="checkbox"
                        checked={
                          editing.delegates
                            .find((d: any) => d.userId === u.id)
                            ?.rights.includes(right) ?? false
                        }
                        onChange={(e) =>
                          setEditing((old: any) => {
                            const delegates = old.delegates.filter((d: any) => d.userId !== u.id),
                              current =
                                old.delegates.find((d: any) => d.userId === u.id)?.rights ?? [],
                              next = e.target.checked
                                ? [...current, right]
                                : current.filter((r: string) => r !== right);
                            if (next.length) delegates.push({ userId: u.id, rights: next });
                            return { ...old, delegates };
                          })
                        }
                      />
                      {t(label)}
                    </label>
                  ))}
                </fieldset>
              ))}
            <button className="primary" disabled={busy}>
              {t('Enregistrer les délégations')}
            </button>
          </form>
        </Modal>
      )}
      {conversion && (
        <Modal title={t('Confirmer la conversion')} onClose={() => setConversion(undefined)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void act(
                () =>
                  send('/admin/mailboxes/' + conversion.id + '/convert', {
                    expectedRevision: conversion.revision,
                    kind: conversion.kind === 'shared' ? 'personal' : 'shared',
                    confirm: true,
                    ...(conversion.kind === 'shared' ? { name, recoveryEmail: contact } : {}),
                  }),
                t('Conversion terminée'),
              ).then((r) => {
                if (r) {
                  setInvitation(r.invitationUrl ?? '');
                  setConversion(undefined);
                }
              });
            }}
          >
            <p>
              <strong>{conversion.address}</strong>{' '}
              {t(
                'conservera ses messages et son quota. Les accès actuels et les délégations seront révoqués.',
              )}
            </p>
            <p>
              {conversion.kind === 'shared'
                ? t(
                    'Une unité disponible sera attribuée et le nouveau titulaire devra activer son compte.',
                  )
                : t(
                    'L’accès direct sera fermé. L’unité reste souscrite et devient réattribuable ; aucun remboursement automatique.',
                  )}
            </p>
            {conversion.kind === 'shared' && (
              <>
                <label>
                  {t('Nom du nouveau titulaire')}
                  <input required value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label>
                  {t('Adresse de récupération indépendante')}
                  <input
                    required
                    type="email"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                  />
                </label>
              </>
            )}
            <button className="primary" disabled={busy}>
              {t('Confirmer la conversion')}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
